import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCheck, ClipboardCheck, Flag, Pencil, Shuffle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full bg-[#04060B] border border-[#D4AF37]/20 rounded-lg px-2 py-1.5 text-sm text-starlight focus:outline-none focus:border-[#D4AF37]/60";
const STATUS = { ok: "Checked OK", fix: "Flagged", corrected: "Corrected" };

function ReadingCheck({ r, kind, headers, onDone }) {
  const [edit, setEdit] = useState(null);
  const [note, setNote] = useState("");
  const post = async (path, body, msg) => {
    try { await axios.post(`${API}/analytics/editorial/${path}`, body, { headers }); toast.success(msg); setEdit(null); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };
  const id = r.sign.toLowerCase();
  return (
    <div data-testid={`editorial-reading-${id}`} className="bg-[#0B0F19] rounded-xl p-3">
      <p className="text-sm text-starlight flex items-center gap-2">{r.sign}{r.status && <span className="text-[11px] gold-text" data-testid={`editorial-status-${id}`}>{STATUS[r.status]}</span>}</p>
      {edit ? (
        <div className="space-y-2 mt-2">
          {["overall", "love", "money"].map((k) => <textarea key={k} rows={3} className={field} value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`editorial-edit-${id}-${k}`} />)}
          <button onClick={() => post("fix", { kind, sign: r.sign, ...edit, note }, `${r.sign} corrected`)} data-testid={`editorial-save-${id}`} className="text-xs bg-[#D4AF37] text-[#04060B] px-3 py-1.5 rounded-full">Save correction</button>
        </div>
      ) : (
        ["overall", "love", "money"].map((k) => <p key={k} className="text-xs text-[#D6D0C5] mt-1"><span className="gold-text uppercase tracking-wider mr-1">{k}</span>{r[k]}</p>)
      )}
      <input className={`${field} mt-2`} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`editorial-note-${id}`} />
      <div className="flex flex-wrap gap-2 mt-2">
        <button onClick={() => post("check", { kind, signs: [r.sign], status: "ok", note }, `${r.sign} logged as checked`)} data-testid={`editorial-ok-${id}`} className="flex items-center gap-1 text-xs border border-[#D4AF37]/40 gold-text px-3 py-1 rounded-full"><ClipboardCheck size={12} /> Looks good</button>
        <button onClick={() => post("check", { kind, signs: [r.sign], status: "fix", note }, `${r.sign} flagged`)} data-testid={`editorial-flag-${id}`} className="flex items-center gap-1 text-xs border border-[#D4AF37]/40 text-[#E8DCC4] px-3 py-1 rounded-full"><Flag size={12} /> Flag</button>
        <button onClick={() => setEdit({ overall: r.overall, love: r.love, money: r.money })} data-testid={`editorial-edit-${id}`} className="flex items-center gap-1 text-xs border border-[#D4AF37]/40 text-[#E8DCC4] px-3 py-1 rounded-full"><Pencil size={12} /> Correct text</button>
      </div>
    </div>
  );
}

function Log({ rows, total }) {
  return (
    <div className="mt-4" data-testid="editorial-log">
      <p className="text-xs text-[#A7B0C0] mb-2">Spot-check log · {total} checks in the last 30 days</p>
      <div className="max-h-56 overflow-y-auto space-y-1">
        {rows.length === 0 ? <p className="text-xs text-[#A7B0C0]" data-testid="editorial-log-empty">No checks logged yet.</p> : rows.map((l) => (
          <p key={l.id} className="text-xs text-[#D6D0C5]">{new Date(l.checked_at).toLocaleString()} · {l.kind} {l.period} · {l.sign} · <span className="gold-text">{STATUS[l.status]}</span>{l.note && ` · ${l.note}`}</p>
        ))}
      </div>
    </div>
  );
}

export default function EditorialChecks({ token }) {
  const headers = { "X-Analytics-Token": token };
  const [kind, setKind] = useState("daily");
  const [all, setAll] = useState(false);
  const [s, setS] = useState(null);
  const [log, setLog] = useState({ log: [], last_30_days: 0 });
  const shown = useRef([]);
  const load = useCallback(async (fresh = false) => {
    const h = { "X-Analytics-Token": token };
    const params = { kind, all };
    if (!fresh && !all && shown.current.length) params.signs = shown.current.join(",");
    const [a, b] = await Promise.all([axios.get(`${API}/analytics/editorial/sample`, { params, headers: h }), axios.get(`${API}/analytics/editorial/log`, { headers: h })]);
    shown.current = a.data.readings.map((r) => r.sign);
    setS(a.data);
    setLog(b.data);
  }, [kind, all, token]);
  useEffect(() => { load(true).catch(() => setS({ readings: [], published: 0, checked: 0 })); }, [load]);
  const bulk = async () => {
    const signs = (await axios.get(`${API}/analytics/editorial/sample`, { params: { kind, all: true }, headers })).data.readings.map((r) => r.sign);
    if (!signs.length) return;
    await axios.post(`${API}/analytics/editorial/check`, { kind, signs, status: "ok", note: "Bulk check" }, { headers });
    toast.success(`${signs.length} ${kind} readings logged as checked`); load();
  };
  return (
    <section data-testid="editorial-checks" className="bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl p-5">
      <h2 className="flex items-center gap-2 text-lg mb-1"><ClipboardCheck size={17} className="gold-text" /> Editorial spot-checks</h2>
      <p className="text-xs text-[#A7B0C0] mb-3">Readings publish automatically. Sample a few, log what you checked, correct anything that's off. Nothing here blocks publishing.</p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {["daily", "weekly", "monthly"].map((k) => <button key={k} onClick={() => setKind(k)} data-testid={`editorial-kind-${k}`} className={`text-xs px-3 py-1.5 rounded-full border ${kind === k ? "bg-[#D4AF37] text-[#04060B] border-[#D4AF37]" : "border-[#D4AF37]/30 text-[#E8DCC4]"}`}>{k}</button>)}
        <button onClick={() => (all ? setAll(false) : load(true))} data-testid="editorial-resample" className="flex items-center gap-1 text-xs gold-text px-2"><Shuffle size={12} /> New sample</button>
        <button onClick={() => setAll(true)} data-testid="editorial-show-all" className={`text-xs px-2 ${all ? "text-starlight" : "gold-text"}`}>Show all 12</button>
        <button onClick={bulk} data-testid="editorial-bulk" className="flex items-center gap-1 text-xs border border-[#D4AF37]/40 gold-text px-3 py-1.5 rounded-full ml-auto"><CheckCheck size={13} /> Mark all as checked (optional)</button>
      </div>
      {s && <p className="text-xs text-[#A7B0C0] mb-2" data-testid="editorial-progress">{s.period} · {s.checked} of {s.published} published {kind} readings checked</p>}
      <div className="space-y-2">{(s?.readings || []).map((r) => <ReadingCheck key={r.sign} r={r} kind={kind} headers={headers} onDone={() => load()} />)}</div>
      {s && !s.readings.length && <p className="text-xs text-[#A7B0C0]" data-testid="editorial-empty">No {kind} readings published yet for this period.</p>}
      <Log rows={log.log} total={log.last_30_days} />
    </section>
  );
}
